import { useMutation } from '@tanstack/react-query';
import { validateZoning as validateZoningAPI } from '../services/zoningValidation.api';
import { reportDeclinedLocation as reportDeclinedLocationAPI } from '../services/declinedLocation.api';

/**
 * Custom hook for zoning validation with automatic declined location reporting
 * Uses React Query's useMutation for proper loading states and error handling
 *
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onSuccess - Called when validation succeeds
 * @param {Function} callbacks.onError - Called when validation fails
 * @param {string} orderId - Order ID (for declined location reporting)
 * @returns {Object} Mutation object with mutate, isLoading, isError, error, data
 */
export function useZoningValidation({ onSuccess, onError, orderId }) {
  return useMutation({
    mutationFn: async (params) => {
      const { lat, lng, state, country, productId } = params;

      if (!lat || !lng || !state || !country || !productId) {
        throw new Error('Missing required zoning validation parameters');
      }

      return validateZoningAPI({
        lat,
        lng,
        state,
        country,
        productId,
        orderId,
      });
    },
    onSuccess: (data, variables) => {
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(data, variables);
      }
    },
    onError: async (error, variables) => {
      // Auto-report declined locations for COUNTRY_NOT_ALLOWED
      if (error.code === 'COUNTRY_NOT_ALLOWED' && orderId && variables.address) {
        try {
          await reportDeclinedLocationAPI({
            orderId,
            address: variables.address,
          });
        } catch (reportError) {
          console.error('Failed to report declined location:', reportError);
        }
      }

      // Call error callback if provided
      if (onError) {
        onError(error, variables);
      }
    },
    retry: false, // Don't retry zoning validation (results won't change)
  });
}
